import type {AssistantFeedbackReasonEnum} from 'src/api/ProactApiClient';
import {AssistantFeedbackRatingEnum} from 'src/api/ProactApiClient';
import {FEEDBACK_REASONS} from 'src/app/assistant/Feedback/messageFeedback';

export const SERIES_DAYS = 30;

export interface OpenTranscript {
  conversationId: number;
  focusMessageId?: number;
}

export type ControlCenterView = 'overview' | 'conversations' | 'feedback';

export const DEFAULT_CONTROL_CENTER_VIEW: ControlCenterView = 'overview';

const CONTROL_CENTER_VIEWS: readonly ControlCenterView[] = [
  'overview',
  'conversations',
  'feedback',
];

export const VIEW_PARAM = 'view';
const RATING_PARAM = 'rating';
const REASONS_PARAM = 'reasons';

export const RATING_SLUGS: Record<AssistantFeedbackRatingEnum, string> = {
  [AssistantFeedbackRatingEnum.Up]: 'up',
  [AssistantFeedbackRatingEnum.Down]: 'down',
};

const SLUG_TO_RATING: Record<string, AssistantFeedbackRatingEnum> = {
  up: AssistantFeedbackRatingEnum.Up,
  down: AssistantFeedbackRatingEnum.Down,
};

// Own-key check so a slug like "toString" never resolves to a prototype member.
export const ratingFromSlug = (
  slug: string | null
): AssistantFeedbackRatingEnum | undefined =>
  slug !== null && Object.prototype.hasOwnProperty.call(SLUG_TO_RATING, slug)
    ? SLUG_TO_RATING[slug]
    : undefined;

export interface FeedbackUrlFilters {
  rating?: AssistantFeedbackRatingEnum;
  reasons: AssistantFeedbackReasonEnum[];
}

export const readControlCenterView = (
  params: URLSearchParams
): ControlCenterView => {
  const value = params.get(VIEW_PARAM);
  return CONTROL_CENTER_VIEWS.find(view => view === value) ??
    DEFAULT_CONTROL_CENTER_VIEW;
};

/** Reasons exist only on Down votes, so any reason forces Down. */
export const normalizeFeedbackFilters = ({
  rating,
  reasons,
}: FeedbackUrlFilters): FeedbackUrlFilters =>
  reasons.length > 0
    ? {rating: AssistantFeedbackRatingEnum.Down, reasons}
    : {rating, reasons};

/** Unknown rating or reason tokens are dropped; the API answers 400 for them. */
export const readFeedbackFilters = (
  params: URLSearchParams
): FeedbackUrlFilters => {
  const reasonSlugs = (params.get(REASONS_PARAM) ?? '').split(',');
  const reasons = FEEDBACK_REASONS.filter(option =>
    reasonSlugs.includes(option.slug)
  ).map(option => option.value);
  return normalizeFeedbackFilters({
    rating: ratingFromSlug(params.get(RATING_PARAM)),
    reasons,
  });
};

/** Params other than the feedback filters (the view tab) are kept. */
export const writeFeedbackFilters = (
  params: URLSearchParams,
  filters: FeedbackUrlFilters
): URLSearchParams => {
  const next = new URLSearchParams(params);
  const {rating, reasons} = normalizeFeedbackFilters(filters);
  if (rating === undefined) next.delete(RATING_PARAM);
  else next.set(RATING_PARAM, RATING_SLUGS[rating]);
  const slugs = FEEDBACK_REASONS.filter(option =>
    reasons.includes(option.value)
  ).map(option => option.slug);
  if (slugs.length === 0) next.delete(REASONS_PARAM);
  else next.set(REASONS_PARAM, slugs.join(','));
  return next;
};

/** Relative `?view=feedback…` link that opens the Feedback tab pre-filtered. */
export const feedbackViewSearch = (
  filters: Partial<FeedbackUrlFilters> = {}
): string => {
  const params = writeFeedbackFilters(
    new URLSearchParams({[VIEW_PARAM]: 'feedback'}),
    {reasons: [], ...filters}
  );
  return `?${params.toString()}`;
};
